/**
 * PersonaLab - Unified Analytics & Telemetry Service
 * Suporta dataLayer (GTM / GA4 / HubSpot / Clarity) e PostHog (Client SDK)
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    _hsq?: unknown[][];
    posthog?: {
      capture: (eventName: string, properties?: Record<string, unknown>) => void;
      identify: (distinctId: string, userProperties?: Record<string, unknown>) => void;
      reset: () => void;
    };
  }
}

import { getHubspotLoginTelemetry, formatHubspotDateTime } from './hubspotService';

export interface UserIdentityData {
  id: string;
  email?: string | null;
  user_metadata?: {
    full_name?: string;
    name?: string;
    avatar_url?: string;
    picture?: string;
    [key: string]: unknown;
  };
  raw_user_meta_data?: {
    full_name?: string;
    name?: string;
    avatar_url?: string;
    picture?: string;
    [key: string]: unknown;
  };
  app_metadata?: {
    provider?: string;
    providers?: string[];
  };
}

export interface ProfileIdentityData {
  full_name?: string | null;
  cargo?: string | null;
  tamanho_empresa?: string | null;
  objetivo_principal?: string | null;
  como_conheceu?: string | null;
  subscription_tier?: string | null;
  trial_ends_at?: string | null;
  subscription_expires_at?: string | null;
  personas_count?: number;
  contexts_count?: number;
}

export interface AnalyticsEventMap {
  // A. Autenticação & Conta
  sign_up: { plan?: string; provider?: string; method?: string } | Record<string, never>;
  login: { provider?: string; method?: string } | Record<string, never>;
  logout: Record<string, never>;
  password_reset: Record<string, never>;
  user_identified: Record<string, unknown>;

  // B. Onboarding & Dashboard
  onboarding_started: Record<string, never>;
  onboarding_completed: Record<string, never>;
  dashboard_checklist_step: { step_name: string };
  dashboard_checklist_completed: Record<string, never>;

  // C. Navegação SPA
  page_view: { page_path: string; page_title: string };

  // D. Monetização & Paywall (Pricing Funnel)
  pricing_modal_opened: { trigger: string; user_tier?: string; trial_days_left?: number };
  pricing_interval_toggled: { billing_interval: 'month' | 'year' };
  pricing_plan_selected: { plan_id: string; billing_interval: 'month' | 'year'; price_value?: number };
  checkout_redirect_started: { plan_id: string; billing_interval: 'month' | 'year'; price_id?: string };
  pricing_modal_closed: { trigger_source?: string };
  plan_compliance_viewed: { excess_personas: number; excess_contexts: number };
  plan_compliance_resolved: { deleted_personas_count: number; deleted_contexts_count: number };

  // E. Personas & Criação
  persona_generation_started: { trigger?: string } | Record<string, never>;
  persona_ai_prompt_submitted: { prompt_length_chars: number };
  persona_generation_completed: { duration_ms?: number; success: boolean };
  persona_generated_ai: Record<string, never>;
  persona_field_edited_manually: { field_name: string };
  persona_avatar_regenerated: { persona_id?: string };
  persona_saved: { is_new: boolean; method: 'ai' | 'manual' | 'edit' };
  persona_deleted: Record<string, never>;

  // F. Contextos / Cenários
  context_created: Record<string, never>;
  context_file_uploaded: { file_type: string; file_size_kb: number };
  context_personas_assigned: { assigned_personas_count: number };
  context_saved: Record<string, never>;
  context_deleted: Record<string, never>;

  // G. Chat & Conversas
  arena_session_started: { persona_id?: string; context_id?: string };
  conversation_created: { mood_id: string; has_context: boolean; has_collection: boolean };
  conversation_mood_help_clicked: Record<string, never>;
  chat_message_sent: { role: 'user' | 'persona' | 'assistant' };
  chat_message_copied: { role?: 'user' | 'persona' | 'assistant'; char_length?: number };

  // H. Insights
  insight_analysis_opened: { messages_count: number };
  insight_generated_ai: { count: number };
  insight_category_filtered: { category_name: string };
  insight_card_copied: { category?: string };
  insight_saved: { category?: string; collection_id?: string | null };
  insight_deleted: Record<string, never>;

  // I. Organização & Busca
  collection_created: Record<string, never>;
  tag_created: Record<string, never>;
  search_executed: { search_query: string; section: 'personas' | 'conversas' | 'insights'; results_count: number };
  filter_applied: { filter_type: string; filter_value: string };

  // J. Ajuda & UX
  contextual_help_opened: { section: string };
  feedback_modal_opened: Record<string, never>;
  menu_settings_clicked: Record<string, never>;
  menu_creators_clicked: Record<string, never>;
}

export type EventName = keyof AnalyticsEventMap;

class AnalyticsService {
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Dispara um evento universal para dataLayer (GTM, GA4, HubSpot, Clarity) e PostHog
   */
  public track<K extends EventName>(eventName: K, properties?: AnalyticsEventMap[K]): void {
    try {
      // 1. DataLayer (GTM / GA4 / HubSpot / Clarity)
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: eventName,
          ...(properties || {}),
        });

        // 2. PostHog Client SDK (se disponível diretamente no window)
        if (window.posthog && typeof window.posthog.capture === 'function') {
          window.posthog.capture(eventName, properties || {});
        }
      }
    } catch (err) {
      console.warn('[AnalyticsService] Erro ao disparar evento:', eventName, err);
    }
  }

  /**
   * Dispara a identificação de usuário enriquecida para CRM (HubSpot), PostHog e GA4
   */
  public identify(user: UserIdentityData, profile?: ProfileIdentityData | null): void {
    try {
      if (typeof window === 'undefined') return;

      const isPaid = profile?.subscription_tier && ['basic', 'pro', 'business'].includes(profile.subscription_tier);
      const isExpired = profile?.subscription_tier === 'expired' || (
        profile?.trial_ends_at && new Date(profile.trial_ends_at).getTime() < Date.now() && !isPaid
      );

      const lifecycleStage = isPaid ? 'customer' : (isExpired ? 'lead' : 'opportunity');
      const subscriptionStatus = isPaid ? 'active_subscriber' : (isExpired ? 'trial_expired' : 'trial_active');

      const rawFullName = (
        profile?.full_name ||
        user.user_metadata?.full_name ||
        user.user_metadata?.name ||
        user.raw_user_meta_data?.full_name ||
        user.raw_user_meta_data?.name ||
        ''
      ).trim();

      const nameParts = rawFullName.split(/\s+/).filter(Boolean);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const loginTelemetry = getHubspotLoginTelemetry(user.id || user.email || undefined);

      const payload = {
        event: 'user_identified',
        userId: user.id,
        userRole: profile?.subscription_tier || 'trial',

        // Dados de CRM / Contato
        userEmail: user.email || '',
        userName: rawFullName || '',
        userFirstName: firstName || '',
        userLastName: lastName || '',
        userJobTitle: profile?.cargo || '',
        companySize: profile?.tamanho_empresa || '',
        mainGoal: profile?.objetivo_principal || '',
        discoverySource: profile?.como_conheceu || '',

        // Telemetria de Login (HubSpot & GTM)
        ultimo_login: formatHubspotDateTime(loginTelemetry.ultimoLogin),
        total_logins: loginTelemetry.totalLogins,
        total_de_logins: loginTelemetry.totalLogins,

        // Dados de Ciclo de Vida e Monetização (HubSpot & PostHog)
        lifecycleStage,
        planTier: profile?.subscription_tier || 'trial',
        subscriptionStatus,
        trialEndsAt: profile?.trial_ends_at || null,
        subscriptionExpiresAt: profile?.subscription_expires_at || null,
        personasCount: profile?.personas_count ?? 0,
        contextsCount: profile?.contexts_count ?? 0,

        // Autenticação
        provider: user.app_metadata?.provider || user.app_metadata?.providers?.[0] || 'email',
      };

      // 1. DataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);

      // 2. PostHog Identify
      if (window.posthog && typeof window.posthog.identify === 'function') {
        window.posthog.identify(user.id, {
          email: user.email || undefined,
          name: rawFullName || undefined,
          first_name: firstName || undefined,
          last_name: lastName || undefined,
          job_title: profile?.cargo || undefined,
          company_size: profile?.tamanho_empresa || undefined,
          main_goal: profile?.objetivo_principal || undefined,
          discovery_source: profile?.como_conheceu || undefined,
          plan_tier: profile?.subscription_tier || 'trial',
          subscription_status: subscriptionStatus,
          lifecycle_stage: lifecycleStage,
          personas_count: profile?.personas_count ?? 0,
          contexts_count: profile?.contexts_count ?? 0,
        });
      }

      // 3. HubSpot Direct Identify Fallback
      if (user.email) {
        window._hsq = window._hsq || [];
        const formattedDate = formatHubspotDateTime(loginTelemetry.ultimoLogin);
        window._hsq.push(['identify', {
          email: user.email,
          firstname: firstName,
          lastname: lastName,
          ultimo_login: formattedDate,
          total_logins: loginTelemetry.totalLogins,
          total_de_logins: loginTelemetry.totalLogins,
        }]);
        window._hsq.push(['trackPageView']);
      }
    } catch (err) {
      console.warn('[AnalyticsService] Erro ao identificar usuário:', err);
    }
  }

  /**
   * Dispara page_view de forma automática no React Router
   */
  public pageView(pagePath: string, pageTitle?: string): void {
    const title = pageTitle || (typeof document !== 'undefined' ? document.title : pagePath);
    this.track('page_view', {
      page_path: pagePath,
      page_title: title,
    });
  }

  /**
   * Dispara busca com debounce de 500ms para evitar spam no tracking
   */
  public trackDebouncedSearch(
    query: string,
    section: 'personas' | 'conversas' | 'insights',
    resultsCount: number,
    delayMs = 500
  ): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    if (!query || query.trim().length === 0) return;

    this.searchDebounceTimer = setTimeout(() => {
      this.track('search_executed', {
        search_query: query.trim(),
        section,
        results_count: resultsCount,
      });
    }, delayMs);
  }

  /**
   * Reset ao fazer logout
   */
  public reset(): void {
    try {
      this.track('logout', {});
      if (typeof window !== 'undefined' && window.posthog && typeof window.posthog.reset === 'function') {
        window.posthog.reset();
      }
    } catch (err) {
      console.warn('[AnalyticsService] Erro no reset:', err);
    }
  }
}

export const analytics = new AnalyticsService();
